import { Signal } from "signals";
import Components from "slotworks/components/components";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import Layers from "slotworks/graphics/layers/layers";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import CancelGroup from "slotworks/utils/contracts/cancel-group";
import Contract from "slotworks/utils/contracts/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import Sequence from "slotworks/utils/contracts/sequence";
import Point from "slotworks/utils/geom/point";
import Rectangle from "slotworks/utils/geom/rectangle";
import { RandomRangeInt } from "slotworks/utils/math/random";
import { Tween } from "slotworks/utils/tween";
import { Easing } from "slotworks/utils/tween";
import ReelComponent from "../../reel-component";

export default class ReelCascader {

    public static RESET_SYMBOLS_ON_SPLICE: boolean = true;
    public static CASCADE_DURATION: number = 500;
    public static CASCADE_DURATION_QUICK_SPIN: number = 250;
    public static CASCADE_DURATION_TURBO_SPIN: number = 150;
    public static SYMBOL_STAGGER_MIN: number = 0;
    public static SYMBOL_STAGGER_MAX: number = 100;

    public onCascadeStart: Signal = new Signal();
    public onCascadeEnd: Signal = new Signal();
    public spliceableSymbols: string[];

    protected config: any;
    protected reel: ReelComponent;
    protected allSymbols: SymbolComponent[];
    protected cancelGroup: CancelGroup = new CancelGroup();

    constructor(reel: ReelComponent, stops: number[], reelset: string[][], symbolsToSplice?: string[]) {
        this.reel = reel;
        this.allSymbols = this.reel.getSymbols();
        this.config = Components.config.matrix.transition;
        this.spliceableSymbols = symbolsToSplice;
    }

    public cascadeSymbols(symbols: Point[], replacementIds: string[], quickSpin: boolean, turboSpin: boolean): Contract<void> {
        return this.cancelGroup.sequence([
            () => Contract.wrap(() => this.onCascadeStart.dispatch(this.reel.index)),
            () => this.spliceSymbols(symbols, replacementIds),
            () => this.cancelGroup.parallel(this.createCascadeContracts(quickSpin, turboSpin)),
            () => Contract.wrap(() => this.onCascadeEnd.dispatch(this.reel.index))
        ]);
    }

    public skip(cascadeRecord: SpinRecord): Contract<void> {
        return new Contract((resolve: (value?: any) => void) => {
            this.cancelGroup.cancel();
            this.resetSymbols(cascadeRecord.stops, cascadeRecord.reelset);
            resolve();
        });
    }

    protected resetSymbols(stops: number[], reelset: string[][]): void {
        this.reel.resetSymbolPositions();
        this.allSymbols.forEach((symbol: SymbolComponent, i: number) => {
            const reelIndex: number = this.reel.index - 1;
            const targetReelset: string[] = reelset[reelIndex];
            const targetStop: number = stops[reelIndex] + i;
            symbol.setSymbol(targetReelset[targetStop]);
            symbol.static();
            symbol.updateTransform();
            symbol.setVisible(true);
        });
    }

    protected createCascadeContracts(quickSpin: boolean, turboSpin: boolean): Array<() => Contract<void>> {
        const cascadeInContracts: Array<() => Contract<void>> = [];
        const cascadeDuration: number = turboSpin ? ReelCascader.CASCADE_DURATION_TURBO_SPIN : quickSpin ? ReelCascader.CASCADE_DURATION_QUICK_SPIN : ReelCascader.CASCADE_DURATION;
        let staggerDelay: number = 0;
        for (let index: number = this.allSymbols.length - 1; index >= 0; index--) {
            const targetRect: Rectangle = Layers.MatrixContent.getPosition(`symbol_${this.reel.index}_${index + 1}`);
            const currentRect: Rectangle = this.allSymbols[index].getTransform();
            if (currentRect.y !== targetRect.y) {
                staggerDelay += RandomRangeInt(ReelCascader.SYMBOL_STAGGER_MIN, ReelCascader.SYMBOL_STAGGER_MAX);
                cascadeInContracts.push(() => this.cascade(index, staggerDelay, cascadeDuration));
            }
        }
        return cascadeInContracts;
    }

    protected cascade(index: number, delay: number, duration: number): Contract<void> {
        const symbol: SymbolComponent = this.allSymbols[index];
        symbol.setVisible(true);
        const transformRect: Rectangle = symbol.getTransform().clone();
        const endTransform: Rectangle = Layers.MatrixContent.getPosition(`symbol_${this.reel.index}_${index + 1}`);
        const tween: Tween = this.cancelGroup.tween(transformRect);
        tween.to({ y: endTransform.y }, duration);
        tween.delay(delay);
        tween.easing(Easing.Cubic.In);
        tween.onUpdate(() => {
            symbol.setTransform(transformRect);
            symbol.updateTransform();
        });
        return new Contract<void>((resolve: (value?: any) => void) => {
            tween.onComplete(() => symbol.land().then(() => resolve(null)));
            tween.start();
        });
    }

    protected spliceSymbols(symbols: Point[], symbolIds: string[]): Contract<void> {
        const rootRect: Rectangle = Layers.MatrixContent.getPosition(`symbol_${this.reel.index}_${1}`);
        const spacingRect: Rectangle = Layers.MatrixContent.getPosition(`symbol_${this.reel.index}_${2}`);
        const spacingY: number = spacingRect.y - rootRect.y;
        const contract: Contract<void> = new Contract<void>((resolve: (value?: any) => void) => {

            symbols.forEach((point: Point, count: number) => {
                const symbolID = this.allSymbols[point.y].symbolId.toUpperCase();

                if (!this.spliceableSymbols || this.spliceableSymbols.indexOf(symbolID) !== -1) {
                    const symbol: SymbolComponent = this.allSymbols.splice(point.y, 1)[0];
                    this.allSymbols.unshift(symbol);
                    const newTransform: Rectangle = new Rectangle(rootRect.x, rootRect.y - (spacingY * (count + 1)), rootRect.width, rootRect.height);
                    symbol.setTransform(newTransform);
                    symbol.updateTransform();
                }
            });

            symbolIds.forEach((id: string, row: number) => {
                const symbol: SymbolComponent = this.reel.getSymbolAt(row);
                symbol.gridPosition.y = row + 1;

                if (ReelCascader.RESET_SYMBOLS_ON_SPLICE) {
                    symbol.setSymbol(id);
                }
            });

            resolve();
        });

        return this.cancelGroup.add(contract);
    }
}
